import { OperationFormHeader } from '../form-header/form-header';

import { OperationLayout } from '@/widgets/operation/ui/operation-layout/operation-layout';
import { Button, chakra } from '@chakra-ui-kraud/react';
import { CustomInput } from '@/shared/components/custom-input/custom-input';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { PreformOperationResponse, PreformOperationUpdateRequest } from '@/shared/state/api/swagger';
import { PreformOperationFillFormProps } from '@/widgets/operation/model/operation.types';
import { useAppSelector } from '@/shared/state/selectors';
import { selectCurrentUserId, selectUserRole } from '@/shared/state/slices';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { preformOperationFillSchema } from '@/widgets/operation/model/operations.schema';
import { mapOperationsById } from '@/widgets/operation/utils';
import { CalendarSinglePicker } from '@/shared/components/calendar-single-picker';
import dayjs from 'dayjs';
import { CustomSelect } from '@/shared/components/custom-select/custom-select';
import styles from '@/shared/components/custom-select/custom-select.module.scss';
import clsx from 'clsx';
import { convertNumberToNumberStringWithComma } from '@/shared/core/utils/convert-string-to-number-string';
import { usePreformOperations } from '@/widgets/operation/hooks/use-preform-operations';
import { ErrorFillOperationAlert } from '@/entities/alerts/error-fill-operation';
import { OperationFillAlert } from '@/entities/alerts/operation-fill';
import { getOperationById } from '@/widgets/operation/utils/map-data';
import { generatePath, useNavigate } from 'react-router-dom';
import { PREFORM_TECHMAP_OPERATION_PATH_UNFILLED, UserRoles } from '@/shared';

type OperationFormValues = Omit<PreformOperationUpdateRequest, 'step_id'> & {
	step_id?: string | number;
};

export const OperationFillFormPreform = ({ techMap, employees }: PreformOperationFillFormProps) => {
	const navigate = useNavigate();
	const [groupVisibility, setGroupVisibility] = useState(false);
	const { handleCurrentOperation, handleNotCurrentOperation } = usePreformOperations();
	const [isSuccess, setIsSuccess] = useState(false);
	const [isError, setIsError] = useState(false);
	const [isFetching, setIsFetching] = useState(false);
	const [skippedOperations, setSkippedOperations] = useState<PreformOperationResponse[]>([]);
	const [stepId, setStepId] = useState(techMap?.current_preform_operation?.step_id);
	const currentUserId = useAppSelector(selectCurrentUserId);
	const userRole = useAppSelector(selectUserRole);
	const mappedOperationsById = mapOperationsById(techMap);

	const isDisabled = userRole === UserRoles.storekeeper;

	const { control, handleSubmit, getValues, setValue } = useForm<OperationFormValues>({
		defaultValues: {
			fio:
				employees.find((employee) => Number(employee.value.split('_')[0]) === currentUserId)?.value ??
				undefined,
			id: techMap?.current_preform_operation?.id,
		},
		resolver: yupResolver(preformOperationFillSchema),
	});

	const opId = useWatch({ control, name: 'id' });

	const onSubmit = (data: OperationFormValues) => {
		setIsFetching(true);

		const fio = employees.find((employee) => employee.value === data.fio)?.label;
		const name = techMap?.preform_operations?.find((operation) => operation.id === Number(data?.id))?.name;

		const payload: PreformOperationUpdateRequest = {
			...data,
			fio,
			name,
			step_id: stepId ?? 0,
			count_in_number: data.count_in_number,
			count_out_number: data.count_out_number,
			wasted: data.wasted
				? Number(data.wasted) > 0 || Number.isNaN(Number(data.wasted))
					? data.wasted
					: '0'
				: '0',
		};

		if (data.id === techMap.current_preform_operation?.id) {
			handleCurrentOperation(payload, techMap)
				.then(() => setIsSuccess(true))
				.catch(() => setIsError(true))
				.finally(() => setIsFetching(false));
		} else {
			handleNotCurrentOperation(payload, techMap)
				.then((skippedOperations: PreformOperationResponse[]) => {
					setSkippedOperations(skippedOperations);
					setIsSuccess(true);
					setIsError(false);
				})
				.catch(() => setIsError(true))
				.finally(() => setIsFetching(false));
		}
	};

	useEffect(() => {
		if (!opId) return;

		const selectedOperation = getOperationById(techMap, opId);
		const { count_in_number, count_out_number, wasted } = selectedOperation ?? {};

		setValue('count_in_number', count_in_number);
		setValue('count_out_number', count_out_number);
		setValue('wasted', wasted ?? undefined);
	}, [opId, setValue, techMap, getValues]);

	useEffect(() => {
		if (skippedOperations?.length && isSuccess) {
			navigate(
				generatePath(PREFORM_TECHMAP_OPERATION_PATH_UNFILLED, {
					preformTechMapId: techMap?.id.toString() ?? '',
				}),
				{
					state: { skippedOperations },
				},
			);
		}
	}, [skippedOperations, isSuccess, techMap?.id, navigate]);

	if (isError) {
		return (
			<ErrorFillOperationAlert
				handleCompleteOperationAgain={() => setIsError(false)}
				variant="preform"
				productTechMapNumber={techMap?.number}
			/>
		);
	}

	if (isSuccess) {
		const submittedOperation = techMap?.preform_operations?.find(
			(operation) => operation.id === Number(getValues('id')),
		)?.name;

		return <OperationFillAlert stepId={stepId} operationName={submittedOperation} />;
	}

	return (
		<OperationLayout
			header={<OperationFormHeader techMap={techMap} variant="preform" />}
			footer={
				<Button width="100%" onClick={handleSubmit(onSubmit)} isDisabled={isFetching || isDisabled}>
					Сохранить
				</Button>
			}
		>
			<chakra.div display="flex" flexDirection="column" gap="20px">
				<Controller
					name="id"
					control={control}
					render={({ field: { value, onChange }, fieldState: { error } }) => (
						<CustomSelect
							disabled={isDisabled}
							onFocus={() => setGroupVisibility(true)}
							onBlur={() => setGroupVisibility(false)}
							showSearch
							allowClear={false}
							options={mappedOperationsById}
							value={value}
							onChange={(id) => {
								setStepId(techMap?.preform_operations?.find((op) => op.id === Number(id))?.step_id);
								onChange(id);
							}}
							filterOption={(input, option) =>
								String(option?.label ?? '')
									.toLowerCase()
									.includes(input.toLowerCase())
							}
							defaultValue={
								mappedOperationsById.find(
									(operation) => operation.value === techMap?.current_preform_operation?.id,
								)?.label
							}
							label="Операция"
							optionFilterProp="children"
							dropdownStyle={{ zIndex: 1400 }}
							size="large"
							className={clsx(
								styles['select'],
								styles['select-large'],
								groupVisibility && styles['select-focus'],
								error && [styles['select-invalid']],
							)}
						/>
					)}
				/>
				<Controller
					name="date"
					control={control}
					render={({ field: { onChange } }) => (
						<CalendarSinglePicker
							isDisabled={isDisabled}
							maxDate={new Date(2050, 1)}
							defaultDate={dayjs().format('YYYY-MM-DD')}
							label="Дата"
							onSelectDate={(date) => onChange(date)}
						/>
					)}
				/>
				<Controller
					name="fio"
					control={control}
					render={({ field: { value, onChange }, fieldState: { error } }) => (
						<CustomSelect
							disabled={isDisabled}
							onFocus={() => setGroupVisibility(true)}
							onBlur={() => setGroupVisibility(false)}
							showSearch
							allowClear={false}
							options={employees}
							value={value}
							onChange={onChange}
							filterOption={(input, option) =>
								String(option?.label ?? '')
									.toLowerCase()
									.includes(input.toLowerCase())
							}
							defaultValue={
								employees.find((employee) => Number(employee.value.split('_')[0]) === currentUserId)
									?.label ?? undefined
							}
							optionFilterProp="children"
							dropdownStyle={{ zIndex: 1400 }}
							size="large"
							label="Исполнитель"
							placeholder="Исполнитель"
							className={clsx(
								styles['select'],
								styles['select-large'],
								groupVisibility && styles['select-focus'],
								error && [styles['select-invalid']],
							)}
						/>
					)}
				/>
				<Controller
					name="count_in_number"
					control={control}
					render={({ field: { onChange, ...field }, fieldState: { error } }) => (
						<CustomInput
							isDisabled={isDisabled}
							size="md"
							{...field}
							isInvalid={!!error}
							value={convertNumberToNumberStringWithComma(String(field.value).replace(/[^0-9,.хx]/g, ''))}
							onChange={(e) => {
								onChange(Number.isNaN(e.target.value) || !e.target.value ? null : e.target.value);
							}}
							label="Выдано в работу м X шт"
							showTooltip={!!error?.message}
							tooltipContent={error?.message}
						/>
					)}
				/>
				<Controller
					name="count_out_number"
					control={control}
					render={({ field: { onChange, ...field }, fieldState: { error } }) => (
						<CustomInput
							isDisabled={isDisabled}
							size="md"
							{...field}
							value={convertNumberToNumberStringWithComma(String(field.value).replace(/[^0-9,.хx]/g, ''))}
							onChange={(e) => {
								onChange(Number.isNaN(e.target.value) || !e.target.value ? null : e.target.value);
							}}
							isInvalid={!!error}
							label="Годных м X шт"
							showTooltip={!!error?.message}
							tooltipContent={error?.message}
						/>
					)}
				/>
				<Controller
					name="wasted"
					control={control}
					render={({ field, fieldState: { error } }) => (
						<CustomInput
							isDisabled={isDisabled}
							labelonTop={!!field.value}
							size="md"
							{...field}
							// @ts-expect-error TODO: fix after swagger update
							value={
								field.value && (Number.isNaN(Number(field.value)) || Number(field.value) > 0)
									? convertNumberToNumberStringWithComma(
											String(field.value).replace(/[^0-9,.хx]/g, ''),
									  )
									: null
							}
							isInvalid={!!error}
							label="Брак м X шт"
							showTooltip={!!error?.message}
							tooltipContent={error?.message}
						/>
					)}
				/>
			</chakra.div>
		</OperationLayout>
	);
};
